import {
  FindingsOrder,
  FindingsOrderField,
  FindingType,
  OrderDirection,
  ScoreRating,
  ScoreStateFilter,
  useGetCvEsQuery,
} from "~/operations";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import { usePaginate } from "~/components/pagination/usePaginate";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { PaginateProps } from "~/components/pagination/paginate";

type UseAssetSoftwareCVEs = {
  assetMrn: string;
  softwareMrn: string;
};

type UseAssetSoftwareVulnerabilitiesReturn = {
  cves: CVEFindingsNode[];
  loading: boolean;
  totalCount: number;
  paginationProps: PaginateProps;
  assetId: string;
  hasError: boolean;
  hasCVEs: boolean;
};

export function useAssetSoftwareVulnerabilities({
  assetMrn,
  softwareMrn,
}: UseAssetSoftwareCVEs): UseAssetSoftwareVulnerabilitiesReturn {
  const paginate = usePaginate();
  const baseUrl = paginate.resetCursorUrl(location.href);

  const sort = useSort<FindingsOrder>({
    baseUrl,
    defaultSort: {
      field: FindingsOrderField.RiskValue,
      direction: OrderDirection.Desc,
    },
    validFields: [
      FindingsOrderField.RiskValue,
      FindingsOrderField.LastUpdated,
      FindingsOrderField.Mrn,
    ],
  });

  const {
    data: softwareCVEsData,
    error: softwareCVEsError,
    loading: softwareCVEsLoading,
  } = useGetCvEsQuery({
    variables: {
      scopeMrn: assetMrn,
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      filter: {
        types: [FindingType.Cve],
        state: ScoreStateFilter.Open,
        rating: [
          ScoreRating.Low,
          ScoreRating.Medium,
          ScoreRating.High,
          ScoreRating.Critical,
        ],
        softwareFindingMrn: softwareMrn,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const cveFindingsUnion =
    softwareCVEsData?.findings?.__typename === "FindingsConnection"
      ? softwareCVEsData?.findings
      : undefined;

  const paginationProps = paginate.pageProps(cveFindingsUnion?.pageInfo);

  const cveFindings: CVEFindingsNode[] = (
    cveFindingsUnion?.edges || []
  ).flatMap((e) => (e?.node?.__typename === "CveFinding" ? e.node : []));

  const hasCVEs = (cveFindings?.length || 0) > 0;
  const assetId = assetMrn.split("/").pop() || "";

  return {
    cves: cveFindings,
    loading: softwareCVEsLoading,
    totalCount: cveFindingsUnion?.totalCount ?? 0,
    paginationProps,
    hasError: !!softwareCVEsError,
    hasCVEs,
    assetId,
  };
}
